import React, { useMemo, useState } from 'react';
import { CTable, CTableHead, CTableRow, CTableHeaderCell, CTableDataCell, CTableBody } from '@coreui/react-pro';
import NoDataTableItem from '../../../components/AdminDashboard/NoDataTableItem';
import { DownArrowIcon, RightArrowLgIcon } from '../../../assets/images';
import type { ICarReport } from '../model/carReport.types';
import StatusChip from './StatusChip';

type SortOrder = 'asc' | 'desc';

export type BackendSortBy =
    | 'ufi'
    | 'category'
    | 'subject'
    | 'creator'
    | 'assigneeLead'
    | 'assigneeGroup'
    | 'status'
    | 'createdAt';

const isClosedLike = (s: string) => {
    const v = (s || '').trim().toLowerCase();
    return v === 'closed' || v === 'closed w/o action' || v === 'closed w/o action';
};

export interface CARReportsTableProps {
    items: ICarReport[];
    onViewReport: (r: ICarReport) => void;

    sortBy?: BackendSortBy;
    sortOrder?: SortOrder;
    onSortChange?: (sortBy: BackendSortBy, sortOrder: SortOrder) => void;

    initialSortBy?: BackendSortBy;
    initialSortOrder?: SortOrder;

    /**
     * Empty state text when there is no row.
     */
    emptyText?: string;
}

export default function CARReportsTable({
    items,
    onViewReport,

    sortBy,
    sortOrder,
    onSortChange,

    initialSortBy = 'createdAt',
    initialSortOrder = 'desc',
    emptyText = 'There is no CAR report data.',
}: CARReportsTableProps) {
    const [innerSortBy, setInnerSortBy] = useState<BackendSortBy>(initialSortBy);
    const [innerSortOrder, setInnerSortOrder] = useState<SortOrder>(initialSortOrder);

    const activeSortBy = sortBy ?? innerSortBy;
    const activeSortOrder = sortOrder ?? innerSortOrder;

    const displayItems = useMemo(() => items ?? [], [items]);

    const headerClickable: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };

    const emitSort = (nextBy: BackendSortBy, nextOrder: SortOrder) => {
        if (onSortChange) onSortChange(nextBy, nextOrder);

        setInnerSortBy(nextBy);
        setInnerSortOrder(nextOrder);
    };

    const toggleSort = (key: BackendSortBy) => {
        if (key === activeSortBy) {
            emitSort(key, activeSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            emitSort(key, 'asc');
        }
    };

    const headSortable = (label: string, key: BackendSortBy, width: number) => (
        <CTableHeaderCell
            style={{
                width,
                ...headerClickable,
                color: '#2C384AF2',
                fontWeight: 400,
                fontSize: 14,
            }}
            onClick={() => toggleSort(key)}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {label}
                <DownArrowIcon
                    width={12}
                    height={12}
                    style={{
                        opacity: activeSortBy === key ? 1 : 0.4,
                        transform: activeSortBy === key && activeSortOrder === 'asc' ? 'rotate(180deg)' : 'none',
                        transition: 'transform .15s, opacity .15s',
                    }}
                />
            </span>
        </CTableHeaderCell>
    );

    const headPlain = (label: string, width: number) => (
        <CTableHeaderCell
            style={{
                width,
                color: '#2C384AF2',
                fontWeight: 400,
                fontSize: 14,
                userSelect: 'none',
            }}
        >
            {label}
        </CTableHeaderCell>
    );

    return (
        <CTable hover responsive align="middle" className="w-100">
            <CTableHead>
                <CTableRow>
                    {headSortable('UFI', 'ufi', 220)}
                    {headSortable('Category', 'category', 170)}
                    {headSortable('Subject', 'subject', 210)}
                    {headPlain('Comment', 320)}
                    {headSortable('Creator', 'creator', 110)}
                    {headSortable('Creation time', 'createdAt', 160)}
                    {headSortable('Assignee lead', 'assigneeLead', 140)}
                    {headSortable('Assignee group', 'assigneeGroup', 150)}
                    {headSortable('Status', 'status', 130)}
                    <CTableHeaderCell style={{ width: 40, color: "#2C384AF2" }} />
                </CTableRow>
            </CTableHead>

            <CTableBody>
                {displayItems.length === 0 ? (

                    <NoDataTableItem colSpan={10} message={"There is no CAR Report data"} />

                ) : (
                    displayItems.map((r, i) => {
                        const muted = isClosedLike(r.status || '');
                        const rowBg = muted ? '#F2F2F3' : 'transparent';
                        const cellBase: React.CSSProperties = {
                            backgroundColor: rowBg,
                            color: '#333333',
                            fontWeight: 400,
                        };

                        return (
                            <CTableRow
                                key={i}
                                onClick={() => onViewReport(r)}
                                style={{
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #E6E6E6',
                                    backgroundColor: rowBg,
                                }}
                            >
                                <CTableHeaderCell style={cellBase}>{r.ufi}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{r.category}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{r.subject}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{r.comment}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{r.creator}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>
                                    {r.creationTime} <span style={{ color: '#808080', fontSize: 12 }}>UTC</span>
                                </CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{r.assigneeLead}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>{(r.assigneeGroup || []).join(', ')}</CTableHeaderCell>
                                <CTableHeaderCell style={cellBase}>
                                    <StatusChip status={r.status || ''} />
                                </CTableHeaderCell>

                                <CTableHeaderCell style={cellBase} align="center">
                                    <RightArrowLgIcon
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            onViewReport(r);
                                        }}
                                        width={'28px'}
                                        height={'28px'}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </CTableHeaderCell>
                            </CTableRow>
                        );
                    })
                )}
            </CTableBody>
        </CTable>
    );
}
